<template>
    <div class="tab-switcher-wrap">
        <div
            ref="track"
            class="tab-switcher"
            role="tablist"
            :aria-label="ariaLabel"
        >
            <button
                v-for="(item, idx) in items"
                :key="item.value !== undefined ? item.value : idx"
                ref="pills"
                type="button"
                class="tab-pill"
                :class="{ 'tab-pill--active': isActive(item, idx) }"
                role="tab"
                :aria-selected="isActive(item, idx)"
                @click="select(item, idx)"
            >
                <v-icon
                    v-if="item.icon"
                    small
                    :color="isActive(item, idx) ? 'primary' : ''"
                    :class="{ 'tab-pill__icon--muted': !isActive(item, idx) }"
                >
                    {{ item.icon }}
                </v-icon>
                <span>{{ item.label }}</span>
            </button>

            <span
                v-if="items.length > 1"
                class="tab-pill-indicator"
                :style="indicatorStyle"
                aria-hidden="true"
            />
        </div>
    </div>
</template>

<script>
export default {
    name: 'TabSwitcher',
    props: {
        value: {
            type: [Number, String, null],
            default: 0,
        },
        items: {
            type: Array,
            required: true,
        },
        ariaLabel: {
            type: String,
            default: 'Modo de trabalho',
        },
    },
    data() {
        return {
            indicator: { left: 0, width: 0 },
            measured: false,
        }
    },
    computed: {
        activeIndex() {
            const idx = this.items.findIndex(item => this.resolveValue(item) === this.value)
            return idx === -1 ? 0 : idx
        },
        indicatorStyle() {
            if (!this.measured) return { opacity: 0 }
            return {
                width: `${this.indicator.width}px`,
                transform: `translateX(${this.indicator.left}px)`,
            }
        },
    },
    watch: {
        value() {
            this.$nextTick(this.updateIndicator)
        },
        items() {
            this.$nextTick(this.updateIndicator)
        },
    },
    mounted() {
        this.$nextTick(this.updateIndicator)
        // Re-measure on viewport changes and when the track itself resizes
        // (e.g. the switcher is revealed inside a dialog after mount).
        window.addEventListener('resize', this.updateIndicator)
        if (typeof ResizeObserver !== 'undefined' && this.$refs.track) {
            this._ro = new ResizeObserver(() => this.updateIndicator())
            this._ro.observe(this.$refs.track)
        }
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.updateIndicator)
        if (this._ro) {
            this._ro.disconnect()
            this._ro = null
        }
    },
    methods: {
        updateIndicator() {
            const pills = this.$refs.pills
            const el = pills && pills[this.activeIndex]
            if (!el || !el.offsetWidth) return
            this.indicator = { left: el.offsetLeft, width: el.offsetWidth }
            this.measured = true
        },
        resolveValue(item) {
            return item.value !== undefined ? item.value : this.items.indexOf(item)
        },
        isActive(item, idx) {
            const v = this.resolveValue(item)
            return v === this.value || (this.value === undefined && idx === 0)
        },
        select(item, idx) {
            const v = this.resolveValue(item)
            if (v === this.value) return
            this.$emit('input', v)
            this.$emit('change', v, idx)
        },
    },
}
</script>

<style scoped>
.tab-switcher-wrap {
    display: flex;
    justify-content: center;
    padding: 18px 0 10px;
}

.tab-switcher {
    position: relative;
    display: inline-flex;
    background: var(--glass-soft-bg);
    border: 1px solid var(--glass-panel-border);
    border-radius: var(--radius-pill);
    padding: 4px;
    box-shadow: inset 0 1px 2px rgba(15, 21, 28, 0.04);
    -webkit-backdrop-filter: blur(8px);
    backdrop-filter: blur(8px);
}

.tab-pill {
    position: relative;
    z-index: 2;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border: none;
    background: transparent;
    font-family: inherit;
    font-size: 13.5px;
    font-weight: 600;
    letter-spacing: 0.01em;
    color: var(--color-on-surface-variant);
    padding: 9px 20px;
    border-radius: var(--radius-pill);
    cursor: pointer;
    transition: color 200ms ease, transform 160ms ease;
    white-space: nowrap;
    min-width: 150px;
    justify-content: center;
}

.tab-pill:hover:not(.tab-pill--active) {
    color: var(--color-on-surface);
}

.tab-pill:focus-visible {
    outline: 2px solid rgba(112, 42, 225, 0.35);
    outline-offset: 2px;
}

.tab-pill--active {
    color: var(--color-primary);
}

.tab-pill__icon--muted {
    color: var(--color-on-surface-variant) !important;
}

.tab-pill-indicator {
    position: absolute;
    top: 4px;
    bottom: 4px;
    left: 0;
    background: var(--color-surface-container-lowest);
    border-radius: var(--radius-pill);
    box-shadow: var(--shadow-soft), 0 2px 6px rgba(116, 47, 229, 0.08);
    transition: transform 280ms cubic-bezier(0.22, 1, 0.36, 1), width 280ms cubic-bezier(0.22, 1, 0.36, 1);
    pointer-events: none;
    z-index: 1;
}

@media (max-width: 560px) {
    .tab-pill {
        min-width: 0;
        padding: 9px 14px;
        font-size: 13px;
    }
}
</style>
